'use client';

import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { ComponentPropsWithoutRef, useContext, useEffect, useRef, useState } from 'react';
import { cn } from '@/shared/lib/cn';
import { Card } from '@/shared/ui/card';
import { Media } from '@/shared/ui/media';
import { useProductDetailGallery } from './product-detail-gallery-wrapper';
import './product-detail.scss';
import { ProductDetailContext } from './provider';

type Props = ComponentPropsWithoutRef<'div'>;
export const ProductDetailGalleryCarousel = ({ ...props }: Props) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [slidesInView, setSlidesInView] = useState<number[]>([]);
    const { currentItem } = useContext(ProductDetailContext);
    const { selectedIndex, setSelectedIndex } = useProductDetailGallery();

    const isActive = slidesInView.length !== 1;

    const [emblaRef, emblaApi] = useEmblaCarousel({
        slidesToScroll: 1,
        direction: 'ltr',
        align: 'start',
        watchDrag: isActive,
        inViewThreshold: 0.9,
    });

    useEffect(() => {
        if (!emblaApi) return;
        const update = (emblaApi: EmblaCarouselType) => {
            setSlidesInView(emblaApi.slidesInView());
        };
        emblaApi.on('slidesInView', update);
        return () => {
            emblaApi.off('slidesInView', update);
        };
    }, [emblaApi]);

    if (!currentItem?.assets?.length || currentItem.assets.length <= 1) {
        return null;
    }

    return (
        <div ref={emblaRef} className="product-detail__gallery-carousel" data-active={isActive} {...props}>
            <div ref={containerRef} className="product-detail__gallery-carousel-slides">
                {currentItem.assets.map((asset, index) => (
                    <Card
                        key={index}
                        as="button"
                        className={cn(
                            'product-detail__gallery-carousel-item',
                            index === selectedIndex && 'product-detail__gallery-carousel-item--active',
                        )}
                        onClick={() => {
                            emblaApi?.scrollTo(index);
                            setSelectedIndex(index);
                        }}
                    >
                        <Media
                            data={{ url: asset.fullUrl ?? '', alt: `Fotka ${index}` }}
                            width={80}
                            height={80}
                            className="product-detail__gallery-carousel-image"
                            data-testid="product-detail-gallery-carousel-image"
                            data-value={asset.fullUrl}
                        />
                    </Card>
                ))}
            </div>
        </div>
    );
};
